import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import {
  Users, DollarSign, UserCheck, UserX, Clock, TrendingUp,
  Search, Filter, MoreVertical, Eye, Ban, RefreshCw,
  LogOut, Shield, ChevronLeft, ChevronRight, AlertCircle,
  Baby, Calendar, Mail, CreditCard, X, CheckCircle
} from "lucide-react";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalSubscribers, setTotalSubscribers] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionModal, setActionModal] = useState(null);
  const [actionReason, setActionReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const pageSize = 20;

  const getAdminToken = () => localStorage.getItem("admin_token");

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/dashboard/stats`, {
        headers: { Authorization: `Bearer ${getAdminToken()}` }
      });
      setStats(res.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        handleLogout();
      }
    }
  }, []);

  const fetchSubscribers = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        skip: currentPage * pageSize,
        limit: pageSize
      });
      if (statusFilter) params.append("status", statusFilter);
      if (searchTerm) params.append("search", searchTerm);

      const res = await axios.get(`${API}/admin/subscribers?${params}`, {
        headers: { Authorization: `Bearer ${getAdminToken()}` }
      });
      setSubscribers(res.data.subscribers);
      setTotalSubscribers(res.data.total);
    } catch (err) {
      console.error("Error fetching subscribers:", err);
    }
  }, [currentPage, statusFilter, searchTerm]);

  useEffect(() => {
    const token = getAdminToken();
    if (!token) {
      navigate("/admin/login");
      return;
    }

    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchStats(), fetchSubscribers()]);
      setLoading(false);
    };
    loadData();
  }, [navigate, fetchStats, fetchSubscribers]);

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");
    navigate("/admin/login");
  };

  const handleAction = async () => {
    if (!actionModal || !selectedUser) return;
    setActionLoading(true);

    try {
      await axios.post(`${API}/admin/subscription/action`, {
        user_id: selectedUser.id,
        action: actionModal,
        reason: actionReason
      }, {
        headers: { Authorization: `Bearer ${getAdminToken()}` }
      });

      // Refresh data
      await Promise.all([fetchStats(), fetchSubscribers()]);
      setActionModal(null);
      setSelectedUser(null);
      setActionReason("");
    } catch (err) {
      alert(err.response?.data?.detail || "Action failed");
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active": return "bg-emerald-500/20 text-emerald-400 border-emerald-500/30";
      case "trial": return "bg-blue-500/20 text-blue-400 border-blue-500/30";
      case "cancelled": return "bg-red-500/20 text-red-400 border-red-500/30";
      case "suspended": return "bg-orange-500/20 text-orange-400 border-orange-500/30";
      case "expired": return "bg-gray-500/20 text-gray-400 border-gray-500/30";
      default: return "bg-gray-500/20 text-gray-400 border-gray-500/30";
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)" }}>
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-amber-500/30 border-t-amber-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)" }}>
      {/* Header */}
      <header className="bg-white/5 border-b border-white/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <Shield size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Bible Buds Admin</h1>
              <p className="text-xs text-gray-400">Subscription Management</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            data-testid="admin-logout-btn"
          >
            <LogOut size={18} />
            <span>Logout</span>
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
          <StatCard icon={Users} label="Total Users" value={stats?.total_users || 0} color="blue" />
          <StatCard icon={UserCheck} label="Active" value={stats?.active_subscriptions || 0} color="emerald" />
          <StatCard icon={Clock} label="Trial" value={stats?.trial_users || 0} color="amber" />
          <StatCard icon={UserX} label="Cancelled" value={stats?.cancelled_subscriptions || 0} color="red" />
          <StatCard icon={Ban} label="Suspended" value={stats?.suspended_subscriptions || 0} color="orange" />
          <StatCard icon={DollarSign} label="MRR" value={`$${stats?.mrr?.toFixed(2) || '0.00'}`} color="green" />
        </div>

        {/* Additional Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
              <CreditCard size={16} />
              <span>Monthly Plans</span>
            </div>
            <p className="text-2xl font-bold text-white">{stats?.monthly_subscribers || 0}</p>
          </div>
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
              <Calendar size={16} />
              <span>Annual Plans</span>
            </div>
            <p className="text-2xl font-bold text-white">{stats?.annual_subscribers || 0}</p>
          </div>
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
              <TrendingUp size={16} />
              <span>New This Month</span>
            </div>
            <p className="text-2xl font-bold text-white">{stats?.recent_signups || 0}</p>
          </div>
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
              <Baby size={16} />
              <span>Total Children</span>
            </div>
            <p className="text-2xl font-bold text-white">{stats?.total_children || 0}</p>
          </div>
        </div>

        {/* Subscribers Table */}
        <div className="bg-white/5 rounded-2xl border border-white/10 overflow-hidden">
          {/* Table Header */}
          <div className="p-4 border-b border-white/10 flex flex-col md:flex-row md:items-center gap-4">
            <h2 className="text-lg font-bold text-white">Subscribers</h2>
            <div className="flex-1 flex flex-col md:flex-row gap-3">
              <div className="relative flex-1 max-w-md">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by email or name..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(0); }}
                  className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  data-testid="admin-search-input"
                />
              </div>
              <div className="relative">
                <Filter size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <select
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(0); }}
                  className="pl-10 pr-8 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500 appearance-none cursor-pointer"
                  data-testid="admin-status-filter"
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="trial">Trial</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="suspended">Suspended</option>
                  <option value="expired">Expired</option>
                </select>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-white/5">
                <tr className="text-left text-gray-400 text-sm">
                  <th className="px-4 py-3 font-medium">User</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Plan</th>
                  <th className="px-4 py-3 font-medium">Children</th>
                  <th className="px-4 py-3 font-medium">Joined</th>
                  <th className="px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {subscribers.map((sub) => (
                  <tr key={sub.id} className="hover:bg-white/5 transition-colors">
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-white font-medium">{sub.name || "No name"}</p>
                        <p className="text-gray-400 text-sm">{sub.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(sub.subscription?.status)}`}>
                        {sub.subscription?.status || "none"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-300">
                      {sub.subscription?.plan || "-"}
                    </td>
                    <td className="px-4 py-3 text-gray-300">
                      {sub.children_count}
                    </td>
                    <td className="px-4 py-3 text-gray-400 text-sm">
                      {formatDate(sub.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setSelectedUser(sub)}
                          className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                          title="View details"
                          data-testid={`view-user-${sub.id}`}
                        >
                          <Eye size={16} />
                        </button>
                        {sub.subscription?.status === "active" || sub.subscription?.status === "trial" ? (
                          <>
                            <button
                              onClick={() => { setSelectedUser(sub); setActionModal("cancel"); }}
                              className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
                              title="Cancel subscription"
                              data-testid={`cancel-user-${sub.id}`}
                            >
                              <UserX size={16} />
                            </button>
                            <button
                              onClick={() => { setSelectedUser(sub); setActionModal("suspend"); }}
                              className="p-2 text-orange-400 hover:text-orange-300 hover:bg-orange-500/10 rounded-lg transition-colors"
                              title="Suspend immediately"
                              data-testid={`suspend-user-${sub.id}`}
                            >
                              <Ban size={16} />
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => { setSelectedUser(sub); setActionModal("reactivate"); }}
                            className="p-2 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 rounded-lg transition-colors"
                            title="Reactivate subscription"
                            data-testid={`reactivate-user-${sub.id}`}
                          >
                            <RefreshCw size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="p-4 border-t border-white/10 flex items-center justify-between">
            <p className="text-gray-400 text-sm">
              Showing {currentPage * pageSize + 1}-{Math.min((currentPage + 1) * pageSize, totalSubscribers)} of {totalSubscribers}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                disabled={currentPage === 0}
                className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={18} />
              </button>
              <span className="text-gray-400 text-sm px-3">
                Page {currentPage + 1} of {Math.ceil(totalSubscribers / pageSize) || 1}
              </span>
              <button
                onClick={() => setCurrentPage(p => p + 1)}
                disabled={(currentPage + 1) * pageSize >= totalSubscribers}
                className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </main>

      {/* Action Confirmation Modal */}
      {actionModal && selectedUser && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
          <div className="bg-[#1a1a2e] rounded-2xl p-6 w-full max-w-md border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white capitalize">{actionModal} Subscription</h3>
              <button
                onClick={() => { setActionModal(null); setSelectedUser(null); setActionReason(""); }}
                className="text-gray-400 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>

            <div className="bg-white/5 rounded-lg p-3 mb-4">
              <p className="text-white font-medium">{selectedUser.name}</p>
              <p className="text-gray-400 text-sm">{selectedUser.email}</p>
            </div>

            {actionModal === "cancel" && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-4">
                <div className="flex items-start gap-2">
                  <AlertCircle size={18} className="text-amber-400 mt-0.5" />
                  <p className="text-amber-300 text-sm">
                    User will retain access until their current billing period ends, then will be blocked from logging in.
                  </p>
                </div>
              </div>
            )}

            {actionModal === "suspend" && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4">
                <div className="flex items-start gap-2">
                  <AlertCircle size={18} className="text-red-400 mt-0.5" />
                  <p className="text-red-300 text-sm">
                    User will be immediately blocked from logging in. Use this for policy violations.
                  </p>
                </div>
              </div>
            )}

            {actionModal === "reactivate" && (
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 mb-4">
                <div className="flex items-start gap-2">
                  <CheckCircle size={18} className="text-emerald-400 mt-0.5" />
                  <p className="text-emerald-300 text-sm">
                    User will be able to log in again immediately.
                  </p>
                </div>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">Reason (optional)</label>
              <textarea
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
                rows={3}
                placeholder="Enter reason for this action..."
                data-testid="action-reason-input"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => { setActionModal(null); setSelectedUser(null); setActionReason(""); }}
                className="flex-1 py-3 bg-white/10 text-white font-medium rounded-xl hover:bg-white/20 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAction}
                disabled={actionLoading}
                className={`flex-1 py-3 font-bold rounded-xl transition-colors disabled:opacity-50 ${
                  actionModal === "reactivate"
                    ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                    : actionModal === "suspend"
                    ? "bg-red-600 hover:bg-red-700 text-white"
                    : "bg-amber-600 hover:bg-amber-700 text-white"
                }`}
                data-testid="confirm-action-btn"
              >
                {actionLoading ? "Processing..." : `Confirm ${actionModal}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Details Modal */}
      {selectedUser && !actionModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
          <div className="bg-[#1a1a2e] rounded-2xl p-6 w-full max-w-lg border border-white/20 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">User Details</h3>
              <button
                onClick={() => setSelectedUser(null)}
                className="text-gray-400 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-white/5 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white font-bold text-lg">
                    {selectedUser.name?.[0] || "?"}
                  </div>
                  <div>
                    <p className="text-white font-bold">{selectedUser.name || "No name"}</p>
                    <p className="text-gray-400 text-sm">{selectedUser.email}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-gray-400">Status</p>
                    <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(selectedUser.subscription?.status)}`}>
                      {selectedUser.subscription?.status || "none"}
                    </span>
                  </div>
                  <div>
                    <p className="text-gray-400">Plan</p>
                    <p className="text-white">{selectedUser.subscription?.plan || "None"}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Joined</p>
                    <p className="text-white">{formatDate(selectedUser.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Children</p>
                    <p className="text-white">{selectedUser.children_count}</p>
                  </div>
                </div>
              </div>

              {selectedUser.subscription?.cancelled_at && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                  <p className="text-red-400 text-sm">
                    <strong>Cancelled:</strong> {formatDate(selectedUser.subscription.cancelled_at)}
                  </p>
                  {selectedUser.subscription.cancellation_reason && (
                    <p className="text-red-300 text-sm mt-1">
                      Reason: {selectedUser.subscription.cancellation_reason}
                    </p>
                  )}
                </div>
              )}

              {selectedUser.subscription?.suspended_at && (
                <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3">
                  <p className="text-orange-400 text-sm">
                    <strong>Suspended:</strong> {formatDate(selectedUser.subscription.suspended_at)}
                  </p>
                  {selectedUser.subscription.suspension_reason && (
                    <p className="text-orange-300 text-sm mt-1">
                      Reason: {selectedUser.subscription.suspension_reason}
                    </p>
                  )}
                </div>
              )}

              {selectedUser.stripe_customer_id && (
                <div className="bg-white/5 rounded-lg p-3">
                  <p className="text-gray-400 text-sm">Stripe Customer ID</p>
                  <p className="text-white text-sm font-mono">{selectedUser.stripe_customer_id}</p>
                </div>
              )}
            </div>

            <button
              onClick={() => setSelectedUser(null)}
              className="w-full mt-6 py-3 bg-white/10 text-white font-medium rounded-xl hover:bg-white/20 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  const colorClasses = {
    blue: "from-blue-500 to-blue-600",
    emerald: "from-emerald-500 to-emerald-600",
    amber: "from-amber-500 to-amber-600",
    red: "from-red-500 to-red-600",
    orange: "from-orange-500 to-orange-600",
    green: "from-green-500 to-green-600"
  };

  return (
    <div className="bg-white/5 rounded-xl p-4 border border-white/10">
      <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${colorClasses[color]} flex items-center justify-center mb-3`}>
        <Icon size={20} className="text-white" />
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-gray-400 text-sm">{label}</p>
    </div>
  );
}
